"use client";

import { FC, useEffect, useRef, useState } from "react";
import Confetti from "react-confetti";
import { Toaster } from "react-hot-toast";
import { app } from "../../services/firebase.ts";
import CompletedRow from "./CompletedRow.tsx";
import Word from "./Word.tsx";

import { AnimationState, State } from "../../models/gameModels.ts";
import GameControl from "./GameControl.tsx";

import { getFirestore } from "@firebase/firestore/lite";
import Row from "antd/lib/row";
import Space from "antd/lib/space";
import Typography from "antd/lib/typography";
import { sizes } from "../../styles/sizes.ts";
import {
  JUMP_ANIMATION_DURATION,
  JUMP_ANIMATION_END_DELAY,
  JUMP_BLOCK_DURATION_GAP,
  LOSE_MODAL_DELAY,
  LOSE_ROW_ANIMATION_DELAY,
  SHAKRE_ANIMATION_DELAY,
  SWAP_ANIMATION_DURATION,
  WIN_MODAL_DELAY,
} from "../../utils/animation-delays.ts";
import { chunk } from "../../utils/list-utils.ts";
import KeyboardControl from "../KeyboardControl.tsx";
import { GameConfig, GameEvents } from "./Game.tsx";
import GameTimer from "./GameTimer.tsx";
import { GameMethods } from "./gameMethods.ts";

export const db = getFirestore(app);

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getPositionsToSwitch = (
  boxesBefore: HTMLButtonElement[],
  boxesAfter: HTMLButtonElement[],
): any[] => {
  const activeBoxes = boxesAfter.filter((i) => !boxesBefore.includes(i));
  const firstRow = boxesBefore.filter((i) => !boxesAfter.includes(i));

  const firstRowPositions = Array.from(firstRow).map((element) =>
    // @ts-ignore
    element.getBoundingClientRect(),
  );
  const activeBoxesPositions = Array.from(activeBoxes).map((element) =>
    // @ts-ignore
    element.getBoundingClientRect(),
  );
  let mappings: any[] = [];
  firstRow.forEach((firstRowBox, index) => {
    if (activeBoxes.includes(firstRowBox)) {
      return;
    }
    if (index < activeBoxes.length) {
      // Ensure there's a corresponding activeBox.
      const activeBox = activeBoxes[index];
      const firstRowBoxPosition = firstRowPositions[index];
      const activeBoxPosition = activeBoxesPositions[index];

      // Create mapping for the firstRow box moving to the activeBox position.
      mappings.push({
        isActiveBox: true,
        box: activeBox,
        positionBefore: activeBoxPosition,
        positionAfter: firstRowBoxPosition,
      });

      // which word will fill the current spot
      mappings.push({
        isActiveBox: false,
        box: firstRow[index],
        positionBefore: firstRowBoxPosition,
        positionAfter: activeBoxPosition,
      });
    }
  });
  return mappings;
};

const GameBoard: FC<{
  state: State;
  fns: GameMethods;
  triggers: GameEvents;
  config?: GameConfig;
}> = ({ state, fns, triggers, config }) => {
  const gridRef = useRef<null | HTMLDivElement>(null);
  config = config || { showAdmin: false, showTimer: false };
  function resetGridStyles(buttons: NodeListOf<HTMLButtonElement>) {
    buttons.forEach((box) => {
      box.style.transition = "none";
      box.style.transform = "none";
    });
  }

  /*
       Animation (or post-animation) rules:
       GUESS-STARTED: Trigger to start animation of guessing an answer
       FINISHED: Submitted an answer, trigger logic of checking it
       LOSE-ANIMATION: An animation to solve the rest of the puzzle since you're a loser
       FINISH-LOSE: Finalize a lose-animation (triggering re-render)
       NONE: No animation is in progress, you may play the game! (be careful from LOSE-ANIMATION!!)
      */

  useEffect(() => {
    if (state.animationInProgress === "GUESS-STARTED" && state.foundGroup) {
      const activeBoxes = Array.from(
        gridRef.current!.children[0].children,
      ) as HTMLButtonElement[];
      const boxesToSwitchWith = Array.from(
        gridRef.current!.querySelectorAll("button[data-active]"),
      ) as HTMLButtonElement[];
      let newPositionsMapping = getPositionsToSwitch(
        activeBoxes,
        boxesToSwitchWith,
      );
      jumpAnimation(boxesToSwitchWith).catch(() => {});
      setTimeout(
        () => {
          solveAnimation(newPositionsMapping, "FINISHED");
        },
        3 * JUMP_BLOCK_DURATION_GAP +
          JUMP_ANIMATION_DURATION +
          JUMP_ANIMATION_END_DELAY,
      );
    }
  }, [state.animationInProgress]);

  useEffect(() => {
    if (state.animationInProgress === "GUESS-STARTED" && !state.foundGroup) {
      mistakeAnimation();
    }
  }, [state.animationInProgress]);

  useEffect(() => {
    if (state.animationInProgress === "FINISHED") {
      fns.finishSubmit();
    } else if (state.animationInProgress === "NONE") {
      resetGridStyles(gridRef.current!.querySelectorAll("button"));
    }
  }, [state.animationInProgress]);

  useEffect(() => {
    if (
      state.animationInProgress === "LOSE-ANIMATION" &&
      state.incomplete.length > 0
    ) {
      setTimeout(() => {
        const firstRow = Array.from(
          gridRef.current!.children[0].children,
        ) as HTMLButtonElement[];
        const words = state.incomplete[0].words;
        const allButtons: HTMLButtonElement[] = Array.from(
          gridRef.current!.querySelectorAll("button"),
        );
        const matchingButtons = allButtons.filter((button) =>
          words.includes(button.textContent || ""),
        );
        let newPositionsMapping = getPositionsToSwitch(
          matchingButtons,
          firstRow,
        );
        solveAnimation(newPositionsMapping, "FINISH-LOSE");
      }, LOSE_ROW_ANIMATION_DELAY);
    } else if (
      state.animationInProgress === "LOSE-ANIMATION" &&
      state.incomplete.length === 0
    ) {
      setTimeout(
        () => fns.endAnimation("GAME-FINISHED-LOSE"),
        LOSE_MODAL_DELAY,
      );
    }
  }, [state.animationInProgress]);

  useEffect(() => {
    if (state.animationInProgress === "WIN") {
      setTimeout(() => fns.endAnimation("GAME-FINISHED-WIN"), WIN_MODAL_DELAY);
    }
  }, [state.animationInProgress]);

  useEffect(() => {
    if (state.animationInProgress === "FINISH-LOSE") {
      fns.solveOne();
      resetGridStyles(gridRef.current!.querySelectorAll("button"));
    }
  }, [state.animationInProgress]);

  const jumpAnimation = async (boxes: HTMLButtonElement[]) => {
    const animate = async (box: HTMLButtonElement) => {
      const sideDuration = JUMP_ANIMATION_DURATION / 2;
      const timeExpression = `${sideDuration / 1000}`;
      box.style.transform = "translate(0, -13px)";
      box.style.transition = `transform ${timeExpression}s ease`;
      await sleep(sideDuration);
      box.style.transform = "translate(0, 0px)";
      box.style.transition = `transform ${timeExpression}s ease`;
      await sleep(sideDuration);
      box.style.transition = "none";
      box.style.transform = "none";
    };
    const promises = [];
    for (const box of boxes) {
      promises.push(animate(box));
      await sleep(JUMP_BLOCK_DURATION_GAP);
    }
    await Promise.all(promises);
  };

  const solveAnimation = (mappings: any[], finalState: AnimationState) => {
    if (mappings.length === 0) {
      fns.endAnimation(finalState);
      return;
    }
    // Apply a CSS transition for movement
    mappings.forEach((boxMapping) => {
      let box = boxMapping.box;
      // box.style.transition = "npone";
      requestAnimationFrame(() => {
        box.style.transform = `translate(
                        ${-boxMapping.positionBefore.left + boxMapping.positionAfter.left}px,
                        ${-boxMapping.positionBefore.top + boxMapping.positionAfter.top}px
                    )`;
        box.style.transition = "transform 1s ease";
      });
    });
    setTimeout(() => {
      fns.deselectAll();
      fns.endAnimation(finalState);
    }, SWAP_ANIMATION_DURATION);
  };

  function mistakeAnimation() {
    const activeBoxes = Array.from(
      gridRef.current!.querySelectorAll("button[data-active]"),
    ) as HTMLButtonElement[];
    activeBoxes.forEach((box: HTMLButtonElement) => {
      box.classList.add("shake");
    });
    setTimeout(() => {
      activeBoxes.forEach((box: HTMLButtonElement) => {
        box.classList.remove("shake");
      });
      fns.endAnimation("FINISHED");
    }, SHAKRE_ANIMATION_DELAY);
  }

  const [shownModal, setShownModal] = useState(false);
  useEffect(() => {
    if (
      state.animationInProgress.includes("GAME-FINISHED") &&
      state.result &&
      !shownModal
    ) {
      triggers.onShowModal?.(state);
      setShownModal(true);
    }
  }, [state.result, state.animationInProgress]);
  return (
    <div>
      <KeyboardControl
        submit={fns.submit.bind(fns)}
        clearBoard={fns.deselectAll.bind(fns)}
        shuffle={fns.shuffle.bind(fns)}
        gridRef={gridRef}
      />
      <GameTimer showing={config.showTimer} game={state} />
      {state.offline && (
        <Typography.Text
          style={{
            fontWeight: "bold",
            textAlign: "center",
            paddingBottom: 16,
          }}
        >
          נראה שאתה מנותק מהרשת, נסה פאזל לדוגמא
        </Typography.Text>
      )}
      <Toaster
        containerStyle={{
          bottom: "10vh",
          fontFamily: "Open Sans",
        }}
      />
      <Space
        direction="vertical"
        style={{ width: sizes.viewWidth }}
        align="center"
      >
        {(state.animationInProgress === "WIN" ||
          state.animationInProgress === "GAME-FINISHED-WIN") && (
          <Confetti recycle={false} numberOfPieces={1000} />
        )}
        <Space
          direction="vertical"
          styles={{
            item: {
              width: sizes.viewWidth,
              padding: 0,
              margin: 0,
            },
          }}
          style={{ width: sizes.viewWidth, padding: 0 }}
          align="center"
        >
          {state.complete.map((group, j) => (
            <CompletedRow key={j} group={group} />
          ))}
          <div id="incomplete" ref={gridRef} aria-multiselectable={true}>
            {chunk(state.items, 4).map((row, i) => (
              <Row key={i} wrap={false}>
                {row.map((item, j) => (
                  <Word
                    key={item}
                    onClick={() => fns.toggleActive(item)}
                    isActive={state.activeItems.includes(item)}
                    col={j}
                    row={i}
                    item={item}
                  />
                ))}
              </Row>
            ))}
          </div>
        </Space>
        <GameControl
          config={config}
          fns={fns}
          state={state}
          triggers={triggers}
        />
      </Space>
    </div>
  );
};

export default GameBoard;
